import React, { useState, useMemo } from 'react';
import { 
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    ComposedChart, Scatter, ReferenceLine
} from 'recharts';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/Settings';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import '../styles/InterestLevelChart.css';

const POINT_RADIUS = 4;
const MONTH_WIDTH = 60; // Width allocated for each month's swarm

// Function to calculate beeswarm positions for points
const calculateBeeswarmPositions = (points, monthWidth = MONTH_WIDTH) => {
    if (!points || points.length === 0) return [];
    
    // Sort points by rating for better distribution
    const sortedPoints = [...points].sort((a, b) => a.rating - b.rating);
    const positions = [];
    const radius = POINT_RADIUS;
    const diameter = radius * 2;
    
    for (const point of sortedPoints) {
        let placed = false;
        let offset = 0;
        let direction = 1;
        let step = 0;
        
        // Try positions until we find a spot that doesn't overlap
        while (!placed) {
            // Check if this position overlaps with any existing points
            const currentX = offset;
            let overlaps = false;
            
            for (const pos of positions) {
                const dx = currentX - pos.xOffset;
                const dy = point.rating - pos.rating;
                const distance = Math.sqrt(dx * dx + dy * dy);
                
                if (distance < diameter) {
                    overlaps = true;
                    break;
                }
            }
            
            if (!overlaps && Math.abs(offset) <= monthWidth / 2 - radius) {
                positions.push({
                    ...point,
                    xOffset: currentX
                });
                placed = true;
            } else {
                // Alternate between positive and negative offsets, increasing distance
                step++;
                direction *= -1;
                offset = step * diameter * direction;
            }
            
            // Prevent infinite loops - if we can't place after many attempts,
            // just place it at the current position
            if (step > 50) {
                positions.push({
                    ...point,
                    xOffset: currentX
                });
                placed = true;
            }
        }
    }
    
    return positions;
};

// Function to calculate box plot statistics
const calculateBoxPlotStats = (values) => {
    if (!values || values.length === 0) return null;
    
    const sorted = [...values].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const median = sorted[Math.floor(sorted.length * 0.5)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;
    const min = Math.max(sorted[0], q1 - 1.5 * iqr);
    const max = Math.min(sorted[sorted.length - 1], q3 + 1.5 * iqr);
    
    return { min, q1, median, q3, max };
};

const InterestLevelChart = ({ 
    data, 
    onPostsSelected,
    selectedTimePeriod,
}) => {
    const [chartSettings, setChartSettings] = useState({
        threshold: "-5",
        operator: '<',
        showRawCounts: false,
        displayMode: 'threshold'
    });
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);

    const handleSettingsClick = (event) => {
        setSettingsAnchorEl(event.currentTarget);
    };

    const handleSettingsClose = () => {
        setSettingsAnchorEl(null);
    };

    const handleSettingChange = (setting, value) => {
        if (setting === 'threshold') {
            if (value === '' || value === '-') {
                setChartSettings(prev => ({
                    ...prev,
                    threshold: value
                }));
                return;
            }
            const numValue = parseFloat(value);
            if (!isNaN(numValue)) {
                setChartSettings(prev => ({
                    ...prev,
                    threshold: value
                }));
            }
        } else {
            setChartSettings(prev => ({
                ...prev,
                [setting]: value
            }));
        }
    };

    // Find the first month with at least 10 data points
    const firstValidMonthIndex = useMemo(() => {
        return data.findIndex(monthData => monthData.posts?.length >= 10);
    }, [data]);

    // Process the data based on current settings
    const processedData = useMemo(() => {
        const thresholdNum = parseFloat(chartSettings.threshold) || 0;
        
        // Filter out months before the first valid month
        const validData = firstValidMonthIndex >= 0 ? data.slice(firstValidMonthIndex) : data;

        return validData.map(monthData => {
            if (!monthData.posts || monthData.posts.length === 0) {
                return {
                    month: monthData.month,
                    hasData: false
                };
            }

            const matchingPosts = monthData.posts.filter(post => 
                chartSettings.operator === '<' ? 
                    post.rating < thresholdNum : 
                    post.rating > thresholdNum
            );

            // For threshold view
            if (chartSettings.displayMode === 'threshold') {
                return {
                    month: monthData.month,
                    hasData: true,
                    percentage: (matchingPosts.length / monthData.posts.length) * 100,
                    count: matchingPosts.length,
                    total: monthData.posts.length,
                    posts: matchingPosts
                };
            }

            // For box plot view
            if (chartSettings.displayMode === 'boxplot') {
                const ratings = monthData.posts.map(post => post.rating).filter(r => r !== null);
                const stats = calculateBoxPlotStats(ratings);
                return {
                    month: monthData.month,
                    hasData: true,
                    ...stats,
                    total: monthData.posts.length
                };
            }

            // For distribution view - create points with beeswarm positions
            const points = monthData.posts
                .filter(post => post.rating !== null)
                .map(post => ({
                    month: monthData.month,
                    rating: post.rating,
                    id: post.id
                }));

            const positionedPoints = calculateBeeswarmPositions(points);

            return {
                month: monthData.month,
                hasData: true,
                points: positionedPoints,
                total: monthData.posts.length
            };
        });
    }, [data, chartSettings.displayMode, chartSettings.operator, chartSettings.threshold, firstValidMonthIndex]);

    const handleChartClick = (data) => {
        if (!data || !data.activePayload || data.activePayload.length === 0) return;

        const clickedPoint = data.activePayload[0].payload;
        if (!clickedPoint || !clickedPoint.month) return;

        const monthData = processedData.find(d => d.month === clickedPoint.month);
        if (!monthData?.posts?.length) return;

        onPostsSelected(monthData.posts, {
            label: `Posts from ${clickedPoint.month} with Interest Change ${chartSettings.operator} ${chartSettings.threshold}`
        });
    };

    // Custom scatter shape for beeswarm points
    const renderScatterPoint = (props) => {
        const { cx, cy, fill } = props;
        return (
            <circle 
                cx={cx} 
                cy={cy} 
                r={POINT_RADIUS}
                fill={fill}
                opacity={0.6}
            />
        );
    };

    return (
        <div className="interest-level-chart">
            <div className="chart-header-with-settings">
                <div>
                    <h2>Interest Level Analysis</h2>
                    <p className="chart-description">
                        {chartSettings.displayMode === 'threshold' 
                            ? `Shows the monthly trend of posts with interest changes ${chartSettings.operator} ${chartSettings.threshold}`
                            : chartSettings.displayMode === 'boxplot'
                            ? 'Shows monthly distribution of interest changes using box plots'
                            : 'Shows the distribution of interest changes by month'
                        }
                    </p>
                </div>
                <IconButton 
                    onClick={handleSettingsClick}
                    size="small"
                    className="settings-button"
                >
                    <SettingsIcon />
                </IconButton>
                <Menu
                    anchorEl={settingsAnchorEl}
                    open={Boolean(settingsAnchorEl)}
                    onClose={handleSettingsClose}
                    className="settings-menu"
                >
                    <div className="settings-menu-content">
                        <FormControl fullWidth size="small" style={{ marginBottom: '1rem' }}>
                            <InputLabel>Display Mode</InputLabel>
                            <Select
                                value={chartSettings.displayMode}
                                onChange={(e) => handleSettingChange('displayMode', e.target.value)}
                                label="Display Mode"
                            >
                                <MenuItem value="threshold">Threshold Analysis</MenuItem>
                                <MenuItem value="distribution">Distribution View</MenuItem>
                                <MenuItem value="boxplot">Box Plot</MenuItem>
                            </Select>
                        </FormControl>
                        
                        {chartSettings.displayMode === 'threshold' && (
                            <>
                                <FormControl fullWidth size="small" style={{ marginBottom: '1rem' }}>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                        value={chartSettings.operator}
                                        onChange={(e) => handleSettingChange('operator', e.target.value)}
                                        label="Operator"
                                    >
                                        <MenuItem value="<">Less than</MenuItem>
                                        <MenuItem value=">">Greater than</MenuItem>
                                    </Select>
                                </FormControl>
                                <TextField
                                    label="Threshold"
                                    type="text"
                                    size="small"
                                    value={chartSettings.threshold}
                                    onChange={(e) => handleSettingChange('threshold', e.target.value)}
                                    fullWidth
                                    style={{ marginBottom: '1rem' }}
                                />
                                <FormControl fullWidth size="small">
                                    <InputLabel>Value Display</InputLabel>
                                    <Select
                                        value={chartSettings.showRawCounts}
                                        onChange={(e) => handleSettingChange('showRawCounts', e.target.value)}
                                        label="Value Display"
                                    >
                                        <MenuItem value={false}>Percentages</MenuItem>
                                        <MenuItem value={true}>Raw Counts</MenuItem>
                                    </Select>
                                </FormControl>
                            </>
                        )}
                    </div>
                </Menu>
            </div>

            <div style={{ width: '100%', height: 500, marginTop: '2rem' }}>
                <ResponsiveContainer>
                    {chartSettings.displayMode === 'threshold' ? (
                        <LineChart
                            data={processedData.filter(d => d.hasData)}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 65
                            }}
                            onClick={handleChartClick}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="month"
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                interval={0}
                            />
                            <YAxis
                                domain={chartSettings.showRawCounts ? [0, 'auto'] : [0, 100]}
                            />
                            <Tooltip
                                formatter={(value) => [
                                    chartSettings.showRawCounts ? 
                                        `${value.toFixed(0)} posts` :
                                        `${value.toFixed(1)}%`,
                                    `Posts with interest change ${chartSettings.operator} ${chartSettings.threshold}`
                                ]}
                                labelFormatter={(label, payload) => {
                                    if (!payload?.[0]?.payload) return `Month: ${label}`;
                                    const total = payload[0].payload.total || 0;
                                    return `Month: ${label} (n=${total})`;
                                }}
                                isAnimationActive={false}
                            />
                            <Line
                                type="monotone"
                                dataKey={chartSettings.showRawCounts ? "count" : "percentage"}
                                stroke="#1976d2"
                                strokeWidth={2}
                                dot={{
                                    r: 4,
                                    fill: "#1976d2",
                                    strokeWidth: 1
                                }}
                                activeDot={{
                                    r: 6,
                                    fill: "#1565c0",
                                    strokeWidth: 1
                                }}
                                isAnimationActive={false}
                            />
                        </LineChart>
                    ) : chartSettings.displayMode === 'boxplot' ? (
                        <ComposedChart
                            data={processedData.filter(d => d.hasData)}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 65
                            }}
                            onClick={handleChartClick}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="month"
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                interval={0}
                            />
                            <YAxis
                                domain={[-10, 10]}
                                ticks={[-10, -5, 0, 5, 10]}
                            />
                            <Tooltip
                                formatter={(value, name) => {
                                    const labels = {
                                        min: 'Minimum',
                                        q1: '25th Percentile',
                                        median: 'Median',
                                        q3: '75th Percentile',
                                        max: 'Maximum'
                                    };
                                    return [`${value.toFixed(1)}`, labels[name] || name];
                                }}
                                labelFormatter={(label, payload) => {
                                    if (!payload?.length) return `Month: ${label}`;
                                    const total = payload[0].payload.total || 0;
                                    return `Month: ${label} (n=${total})`;
                                }}
                            />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            {/* Box plot elements */}
                            <Line type="monotone" dataKey="min" stroke="#1976d2" dot={false} />
                            <Line type="monotone" dataKey="q1" stroke="#1976d2" strokeWidth={2} />
                            <Line type="monotone" dataKey="median" stroke="#1976d2" strokeWidth={2} />
                            <Line type="monotone" dataKey="q3" stroke="#1976d2" strokeWidth={2} />
                            <Line type="monotone" dataKey="max" stroke="#1976d2" dot={false} />
                        </ComposedChart>
                    ) : (
                        <ComposedChart
                            data={processedData.flatMap(month => 
                                month.points?.map(point => ({
                                    ...point,
                                    // Adjust x position by adding the offset to the month's base position
                                    x: point.xOffset
                                })) || []
                            )}
                            margin={{
                                top: 20,
                                right: 30,
                                left: 20,
                                bottom: 65
                            }}
                            onClick={handleChartClick}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="month"
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                interval={0}
                                type="category"
                            />
                            <YAxis
                                domain={[-10, 10]}
                                ticks={[-10, -5, 0, 5, 10]}
                            />
                            <Tooltip
                                formatter={(value) => [`Rating: ${value.toFixed(1)}`, 'Interest Change']}
                                labelFormatter={(label, payload) => {
                                    if (!payload?.length) return `Month: ${label}`;
                                    const monthData = processedData.find(d => d.month === label);
                                    const total = monthData?.total || 0;
                                    return `Month: ${label} (n=${total})`;
                                }}
                                isAnimationActive={false}
                            />
                            <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                            <Scatter
                                dataKey="rating"
                                shape={renderScatterPoint}
                                fill="#1976d2"
                            />
                        </ComposedChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default InterestLevelChart; 